// Content for the secondary/info pages.
// The legal pages below are written specifically for QuizzyLoot (a free,
// ad-supported browser trivia game with no accounts and local-only score
// storage). They are genuine and accurate to how the app works; you should
// still have your own counsel review them before launch, and confirm the
// contact address / effective date.

export const QUIZ_RULES = {
  title: 'How to Play',
  intro: 'How a round works',
  items: [
    'Each round has 5 multiple-choice questions from the category you pick.',
    'You have a short timer on every question — answer before it runs out.',
    'Each question has 3 options, and exactly one is correct.',
    'You earn virtual coins for every correct answer; a wrong answer earns none.',
    'Stuck? Use a free hint (watch a short video) or spend some coins to remove a wrong option.',
    'QuizzyLoot is a free, for-fun trivia game. Coins are an in-game score only — there are no prizes, winners, entry fees, or cash rewards.',
  ],
  footer: 'For full details, please refer to our Terms & Conditions.',
};

export const CONTACT = {
  title: 'Contact Us',
  blocks: [
    {
      heading: 'Support',
      body: 'For any queries or feedback, reach us at support@quizzyloot.com',
    },
    {
      heading: 'Are you an app / web publisher?',
      body: 'Want to integrate QuizzyLoot to grow your engagement and revenue? Get in touch at partners@quizzyloot.com',
    },
  ],
};

const UPDATED = 'Last updated: June 6, 2026';

export const PRIVACY = {
  title: 'Privacy Policy',
  intro: UPDATED,
  sections: [
    {
      heading: 'About QuizzyLoot',
      body: 'QuizzyLoot is a free, browser-based trivia game at quizzyloot.com. This policy explains what information is involved when you play and how it is used. You can reach us at support@quizzyloot.com.',
    },
    {
      heading: 'Information We Collect',
      body: 'We do not ask you to create an account or provide personal details such as your name or email to play. The only data you generate in the game — your virtual coin score — is stored on your own device (see “Local storage” below) and is not sent to us. Like most websites, our hosting and security providers may automatically process limited technical data such as your IP address, browser type and pages requested in standard server logs.',
    },
    {
      heading: 'Local storage on your device',
      body: 'Your coin score and a few preferences are saved in your browser using localStorage. This stays on your device, is not transmitted to us, and you can clear it at any time through your browser settings.',
    },
    {
      heading: 'Advertising and Google',
      body: 'QuizzyLoot is supported by advertising. We use Google’s Ad Placement API / Google AdSense to display ads. Google and its advertising partners use cookies, device identifiers and similar technologies to serve and measure ads, including personalised ads where permitted. Their use of this data is governed by Google’s Privacy Policy and “How Google uses information from sites or apps that use our services” (policies.google.com/technologies/partner-sites).',
    },
    {
      heading: 'Your ad choices',
      body: 'You can review and control personalised advertising at Google Ads Settings (adssettings.google.com), and opt out of personalised ads from many vendors at aboutads.info/choices or, in Europe, youronlinechoices.eu. You can also block or delete cookies in your browser settings.',
    },
    {
      heading: 'Consent (EEA, UK and similar regions)',
      body: 'Where required by law, users in the EEA, UK and certain other regions are asked to consent to advertising cookies before they are used, and may withdraw consent at any time. If you do not consent, you may see only non-personalised ads.',
    },
    {
      heading: 'Children',
      body: 'QuizzyLoot is a general-audience game and is not directed to children under 13 (or the minimum age required in your country). We do not knowingly collect personal information from children. If you believe a child has provided personal data, contact us and we will address it.',
    },
    {
      heading: 'Your rights',
      body: 'Depending on where you live (for example under the GDPR or CCPA), you may have rights to access, correct or delete personal data. Because we do not maintain user accounts and your score lives on your device, most data is either controllable by you locally or held by our advertising partners — for ad-related data, please also use the Google controls above. To exercise a right or ask a question, email support@quizzyloot.com.',
    },
    {
      heading: 'Changes & contact',
      body: 'We may update this policy from time to time; the “last updated” date above will change accordingly. Questions? Email support@quizzyloot.com.',
    },
  ],
};

export const TERMS = {
  title: 'Terms & Conditions',
  intro: UPDATED,
  sections: [
    {
      heading: 'Acceptance of these terms',
      body: 'By using QuizzyLoot you agree to these terms. If you do not agree, please do not use the game.',
    },
    {
      heading: 'The service',
      body: 'QuizzyLoot is a free trivia game provided for entertainment. We may add, change or remove quizzes, mini-games and features at any time.',
    },
    {
      heading: 'Virtual coins — no monetary value',
      body: 'Coins are a virtual, in-game score with no real-world monetary value. They cannot be purchased, withdrawn, transferred, or exchanged for cash or prizes. QuizzyLoot is not a contest, sweepstakes, lottery, or gambling service, and there are no real winners or rewards.',
    },
    {
      heading: 'Acceptable use',
      body: 'Please play fairly. Do not use bots or automation, exploit or interfere with the game or its advertising, attempt to disrupt the service, or use it for any unlawful purpose. We may limit or end access for misuse.',
    },
    {
      heading: 'Advertising',
      body: 'The game is funded by advertising. Some optional features — such as doubling coins or getting a free hint — let you watch a short ad in exchange. Watching ads is always your choice.',
    },
    {
      heading: 'Intellectual property',
      body: 'The QuizzyLoot name, design, text and game content are owned by us or our licensors and may not be copied or reused without permission. Emoji are provided by the operating system or browser.',
    },
    {
      heading: 'Disclaimer & liability',
      body: 'The game is provided “as is” without warranties of any kind. To the fullest extent permitted by law, we are not liable for any indirect or incidental damages arising from your use of the game.',
    },
    {
      heading: 'Changes & contact',
      body: 'We may update these terms from time to time; continued use means you accept the updated terms. Questions? Email support@quizzyloot.com.',
    },
  ],
};

export const COOKIES = {
  title: 'Cookie Policy',
  intro: UPDATED,
  sections: [
    {
      heading: 'What cookies and similar technologies are',
      body: 'Cookies are small files stored on your device. Along with similar technologies such as localStorage and device identifiers, they let a site remember information between visits.',
    },
    {
      heading: 'How QuizzyLoot uses them',
      body: 'We use localStorage to remember your coin score and basic preferences on your device. Our advertising provider, Google, and its partners use cookies and similar identifiers to serve and measure ads. We do not use cookies to build a personal profile of you ourselves.',
    },
    {
      heading: 'Advertising cookies',
      body: 'Ads are provided by Google’s Ad Placement API / AdSense. Google and its partners may set advertising cookies as described in “How Google uses information from sites or apps that use our services” (policies.google.com/technologies/partner-sites). You can manage personalised ads at adssettings.google.com, aboutads.info/choices, or youronlinechoices.eu.',
    },
    {
      heading: 'Managing cookies',
      body: 'You can block or delete cookies and clear localStorage through your browser settings, and (where offered) change your consent choice at any time. Disabling some cookies may affect how parts of the game work.',
    },
  ],
};
